/**
 * Poll Lambda CloudWatch logs via Events API (see deviceCloudBackend fleet_common.lambda_log_stream).
 */

export type LambdaLogEvent = {
  timestampMs: number;
  message: string;
  ingestionTimeMs?: number;
};

export type LambdaLogChunk = {
  events: LambdaLogEvent[];
  nextToken?: string | null;
};

export type LambdaLogPollFn = (nextToken?: string | null) => Promise<LambdaLogChunk>;

export type LambdaLogStreamOptions = {
  onLine: (line: string, event: LambdaLogEvent) => void;
  onDone?: (reason: 'complete' | 'error' | 'aborted') => void;
  pollIntervalMs?: number;
  shouldStop?: () => boolean | Promise<boolean>;
};

export class LambdaLogStreamPoller {
  private timer: ReturnType<typeof setInterval> | null = null;

  private aborted = false;

  private seenKeys = new Set<string>();

  private inFlight = false;

  private nextToken: string | null = null;

  constructor(
    private readonly pollFn: LambdaLogPollFn,
    private readonly options: LambdaLogStreamOptions,
  ) {}

  start(): void {
    this.aborted = false;
    const interval = Math.max(500, this.options.pollIntervalMs ?? 1500);
    void this.tick();
    this.timer = setInterval(() => {
      void this.tick();
    }, interval);
  }

  stop(reason: 'complete' | 'error' | 'aborted' = 'aborted'): void {
    this.aborted = true;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.options.onDone?.(reason);
  }

  private lineKey(ev: LambdaLogEvent): string {
    return `${ev.timestampMs}:${ev.ingestionTimeMs ?? 0}:${ev.message}`;
  }

  private emitNewEvents(events: LambdaLogEvent[]): void {
    const sorted = [...events].sort((a, b) => a.timestampMs - b.timestampMs);
    for (const ev of sorted) {
      const key = this.lineKey(ev);
      if (this.seenKeys.has(key)) continue;
      this.seenKeys.add(key);
      this.options.onLine(ev.message, ev);
    }
  }

  private async tick(): Promise<void> {
    if (this.aborted || this.inFlight) return;
    this.inFlight = true;
    try {
      if (this.options.shouldStop) {
        const stop = await this.options.shouldStop();
        if (stop) {
          this.stop('complete');
          return;
        }
      }
      const chunk = await this.pollFn(this.nextToken);
      this.emitNewEvents(chunk.events ?? []);
      this.nextToken = chunk.nextToken ?? null;
    } catch {
      this.stop('error');
    } finally {
      this.inFlight = false;
    }
  }
}
